/**
 * Admin des Moves (`/tutor/admin/moves`, migration 0752) : lire le catalogue en base, enregistrer un Move,
 * en créer un, régler ses SKILLCARDS associées. Écriture directe sous la RLS admin (`move_catalog:
 * admin_write`, `move_cards: admin_write`).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { GESTURE_KEYS, moveIssues, type MoveDef } from '@/features/moves/move-catalog';
import {
  fetchMoveCatalog,
  MOVE_CATALOG_KEY,
  moveRowSchema,
  moveToRow,
} from '@/features/moves/move-catalog-store';

// tables postérieures aux types générés
const db = supabase as unknown as SupabaseClient;

export const MOVE_ID_RE = /^[a-z][a-z0-9-]{1,40}$/;

export const ADMIN_MOVES_KEY = ['admin-move-catalog'] as const;

/** Tout le catalogue, Moves inactifs compris. */
export function useAdminMoves() {
  return useQuery({ queryKey: ADMIN_MOVES_KEY, queryFn: fetchMoveCatalog });
}

/**
 * Ce qui empêche d'enregistrer un Move : les règles du catalogue (`moveIssues`), l'identifiant, et le
 * schéma de la base (les mêmes bornes que le CHECK SQL). PUR.
 */
export function moveFormIssues(m: MoveDef, all: readonly MoveDef[], isNew: boolean): string[] {
  const out = [...moveIssues(m)];
  if (!MOVE_ID_RE.test(m.id))
    out.push('identifiant : minuscules, chiffres et tirets (2 à 41 caractères)');
  if (isNew && all.some((x) => x.id === m.id)) out.push('cet identifiant existe déjà');
  if (!m.levels.length) out.push('niveau minimum à choisir');
  if (!m.name.trim()) out.push('libellé à écrire');
  if (m.kind === 'savoir' && m.writes && m.writes.length > 200)
    out.push('ligne posée : 200 caractères au plus');
  if (m.choices.length > 8) out.push('8 écritures au plus');
  if (m.choices.some((c) => !c.text.trim())) out.push('une écriture du déblocage est vide');
  if (m.kind === 'geste' && m.gesture) {
    const twin = all.find((x) => x.id !== m.id && x.kind === 'geste' && x.gesture === m.gesture);
    if (twin) out.push(`la famille de touches est déjà ouverte par « ${twin.name} »`);
  }
  if (m.placement && m.gesture === 'commute')
    out.push('les flèches ← → de « Commuter » restent dans le dock');
  const row = moveRowSchema.safeParse({ move_id: m.id, ...moveToRow(m) });
  if (!row.success) out.push('valeurs hors des bornes de la base');
  return [...new Set(out)];
}

/** Les touches qu'ouvre un geste (pour l'aide de l'admin). PUR. */
export function gestureKeysLabel(m: MoveDef): string {
  return m.gesture ? GESTURE_KEYS[m.gesture].join(', ') : '';
}

/** Enregistre un Move (le crée s'il est nouveau) et ses SKILLCARDS associées. */
export function useSaveMove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ move, isNew }: { move: MoveDef; isNew: boolean }) => {
      const row = moveToRow(move);
      const res = isNew
        ? await db.from('move_catalog').insert({ move_id: move.id, ...row })
        : await db.from('move_catalog').update(row).eq('move_id', move.id);
      if (res.error) throw new Error(res.error.message);

      const { data: links, error } = await db
        .from('move_cards')
        .select('card_id')
        .eq('move_id', move.id);
      if (error) throw new Error(error.message);
      const before = new Set(((links ?? []) as { card_id: string }[]).map((l) => l.card_id));
      const after = new Set(move.cardIds);
      const gone = [...before].filter((id) => !after.has(id));
      const added = [...after].filter((id) => !before.has(id));
      if (gone.length) {
        const d = await db.from('move_cards').delete().eq('move_id', move.id).in('card_id', gone);
        if (d.error) throw new Error(d.error.message);
      }
      if (added.length) {
        const i = await db
          .from('move_cards')
          .insert(added.map((card_id) => ({ move_id: move.id, card_id })));
        if (i.error) throw new Error(i.error.message);
      }
    },
    onSettled: async () => {
      // l'admin ET le catalogue que l'app pose dans le code (la trousse le relit)
      await qc.invalidateQueries({ queryKey: ADMIN_MOVES_KEY });
      await qc.invalidateQueries({ queryKey: MOVE_CATALOG_KEY });
    },
  });
}

/** Un Move vierge, pour la création (un savoir par défaut : il se crée entièrement depuis l'admin). */
export function blankMove(position: number): MoveDef {
  return {
    id: '',
    kind: 'savoir',
    name: '',
    why: '',
    writes: '',
    choices: [
      { text: '', ok: true, why: 'Oui.' },
      { text: '', why: '' },
      { text: '', why: '' },
    ],
    levels: ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'],
    hora: 100,
    type: 'formula',
    active: false,
    unlockedByDefault: false,
    placement: null,
    script: null,
    cardIds: [],
    position,
    notes: '',
  };
}
